import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  HardDrive, Cloud, Download, RefreshCw, Clock, 
  CheckCircle, AlertTriangle, Database, FolderOpen
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminBackupsPage() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backupInProgress, setBackupInProgress] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statusRes, historyRes] = await Promise.all([
        fetch(`${API_URL}/api/backup/status`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/backup/history?limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (statusRes.ok) setStatus(await statusRes.json());
      if (historyRes.ok) {
        const data = await historyRes.json();
        setHistory(data.backups || []);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const runBackupNow = async () => {
    setBackupInProgress(true);
    try {
      const res = await fetch(`${API_URL}/api/backup/run-now`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Backup lancé ! Vérifiez votre Google Drive dans quelques minutes.');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setBackupInProgress(false);
    }
  };

  const downloadZip = () => {
    window.open(`${API_URL}/api/backup/download-zip`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="admin-backups-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <HardDrive className="w-6 h-6 text-primary" />
            Sauvegardes
          </h1>
          <p className="text-muted-foreground">
            Gestion des backups automatiques sur Google Drive
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadZip}>
            <Download className="w-4 h-4 mr-2" />
            Télécharger ZIP
          </Button>
          <Button 
            onClick={runBackupNow}
            disabled={backupInProgress}
            className="bg-primary"
          >
            {backupInProgress ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Cloud className="w-4 h-4 mr-2" />
            )}
            Backup maintenant
          </Button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className={status?.drive_connected ? 'border-green-200' : 'border-red-200'}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Cloud className={`w-5 h-5 ${status?.drive_connected ? 'text-green-600' : 'text-red-600'}`} />
                <span className="font-medium">Google Drive</span>
              </div>
              <Badge className={status?.drive_connected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                {status?.drive_connected ? 'Connecté' : 'Non connecté'}
              </Badge>
            </div>
            {!status?.drive_connected && (
              <p className="text-xs text-muted-foreground mt-2">
                Connectez-vous à Google Drive dans Paramètres → Intégrations
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-600" />
              <span className="font-medium">Collections sauvegardées</span>
            </div>
            <p className="text-2xl font-bold mt-2">{status?.collections_backed_up?.length || 0}</p>
            <p className="text-xs text-muted-foreground">
              CRM, Parcellaire, RH, Factures, etc.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              <span className="font-medium">Backups effectués</span>
            </div>
            <p className="text-2xl font-bold mt-2">{status?.total_backups || 0}</p>
            <p className="text-xs text-muted-foreground">
              Total historique
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Collections */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5" />
            Données sauvegardées
          </CardTitle>
          <CardDescription>
            Liste des collections automatiquement sauvegardées sur Google Drive
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {(status?.collections_backed_up || [
              'crm_contacts', 'parcelles', 'interventions', 'equipe',
              'factures', 'stocks', 'materiel', 'irrigation_reseaux',
              'irrigation_sessions', 'documents_reglementaires', 'elevage_animaux'
            ]).map((collection) => (
              <div 
                key={collection}
                className="p-3 rounded-lg bg-muted/50 flex items-center gap-2"
              >
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm capitalize">{collection.replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Structure Drive */}
      <Card>
        <CardHeader>
          <CardTitle>Structure des dossiers Google Drive</CardTitle>
          <CardDescription>
            Organisation des fichiers sur votre Drive
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="font-mono text-sm bg-muted p-4 rounded-lg">
            <div className="text-primary font-bold">📁 Bureau Agricole/</div>
            <div className="ml-4">
              <div className="text-muted-foreground">📁 [Nom de la société]/</div>
              <div className="ml-4 text-xs text-muted-foreground">
                <div>📁 CRM/</div>
                <div>📁 Parcellaire/</div>
                <div>📁 RH/</div>
                <div>📁 Factures/</div>
                <div>📁 Interventions/</div>
                <div>📁 Stocks/</div>
                <div>📁 Irrigation/</div>
                <div>📁 Documents Réglementaires/</div>
                <div>📁 Élevage/</div>
                <div>📁 Backups/</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Historique */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Historique des backups
          </CardTitle>
          <CardDescription>
            Derniers backups effectués
          </CardDescription>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <HardDrive className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun backup effectué</p>
              <p className="text-sm">Cliquez sur "Backup maintenant" pour lancer votre premier backup</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((backup, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 rounded-lg border"
                >
                  <div className="flex items-center gap-3">
                    {backup.errors?.length > 0 ? (
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                    <div>
                      <p className="font-medium">{backup.societe_nom || 'Backup'}</p>
                      <p className="text-xs text-muted-foreground">
                        {backup.timestamp ? new Date(backup.timestamp).toLocaleString('fr-FR') : '-'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">
                      {backup.files_backed_up?.length || 0} fichiers
                    </p>
                    {backup.errors?.length > 0 && (
                      <p className="text-xs text-amber-600">{backup.errors.length} erreurs</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Programmation */}
      <Card>
        <CardHeader>
          <CardTitle>Backup automatique</CardTitle>
          <CardDescription>
            Configuration des sauvegardes automatiques
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 rounded-lg bg-blue-50 border border-blue-200">
            <div className="flex items-center gap-3">
              <Clock className="w-6 h-6 text-blue-600" />
              <div>
                <p className="font-medium text-blue-800">Backup hebdomadaire</p>
                <p className="text-sm text-blue-600">Chaque dimanche à 3h00 UTC</p>
              </div>
            </div>
            <Badge className="bg-blue-100 text-blue-800">Actif</Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Toutes les données de chaque société sont automatiquement sauvegardées sur leur Google Drive respectif.
            Les backups sont conservés pendant 4 semaines.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
